import React from 'react';
import { useSingleSaleTableItemTaxesSelectorStyles } from 'components/Payments/SingleSale/Table/Item/Taxes/Selector/SingleSaleTableItemTaxesSelector.styles';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener, Typography } from '@material-ui/core';
import { useMarketplace } from 'hooks';
import { MarketplaceTax } from '@spike/marketplace-model';
import clsx from 'clsx';

interface SingleSaleTableItemTaxesSelectorProps {
    readOnly?: boolean;
    options?: Array<MarketplaceTax>;
    selectedTaxIds?: Array<number>;
    className?: string;
    onSelect?: (tax: MarketplaceTax | undefined) => void;
}

export const SingleSaleTableItemTaxesSelector: React.FC<SingleSaleTableItemTaxesSelectorProps> = props => {
    const classes = useSingleSaleTableItemTaxesSelectorStyles();

    const marketplace = useMarketplace();

    const [expanded, setExpanded] = React.useState(false);

    const taxesOptions: Array<MarketplaceTax> = React.useMemo(() => {
        return (props.options?.length === 0 ? marketplace.taxes : props.options) as Array<MarketplaceTax>;
    }, [props.options, marketplace.taxes]);

    const selectedTaxes = React.useMemo(() => {
        return taxesOptions.filter(tax => props.selectedTaxIds?.includes(Number(tax.id)));
    }, [props.selectedTaxIds, taxesOptions]);

    const taxes = React.useMemo(() => {
        if (props.readOnly) {
            return selectedTaxes;
        }

        return props.selectedTaxIds?.length === 0
            ? taxesOptions
            : [
                  { id: 0, uuid: '0', name: 'No Tax' },
                  ...taxesOptions.filter(tax => !props.selectedTaxIds?.includes(Number(tax.id)))
              ];
    }, [props.readOnly, props.selectedTaxIds, selectedTaxes, taxesOptions]);

    const list = (
        <ClickAwayListener onClickAway={() => setExpanded(false)}>
            <div className={classes.list}>
                {taxes.map(tax => (
                    <div
                        key={tax.uuid}
                        className={classes.taxContainer}
                        onClick={() =>
                            props.onSelect && (tax.id === 0 ? props.onSelect(undefined) : props.onSelect(tax))
                        }
                    >
                        <Typography className={classes.tax}>
                            {tax.id === 0 ? 'No Tax' : `${tax.name} ${tax.percentage}%`}
                        </Typography>
                    </div>
                ))}
            </div>
        </ClickAwayListener>
    );

    return (
        <div
            className={clsx(classes.container, props.className)}
            onClick={() => setExpanded(prev => !prev)}
        >
            <Typography className={classes.selectedTax}>
                {selectedTaxes.length === 0 && 'No Tax'}
                {selectedTaxes.length === 1 && `${selectedTaxes[0].percentage}%`}
                {selectedTaxes.length > 1 && 'Multiple'}
            </Typography>

            <FontAwesomeIcon
                icon={expanded ? faChevronUp : faChevronDown}
                className={classes.icon}
            />
            {expanded && list}
        </div>
    );
};
